import type { ReactNode } from "react";

import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import { extractSubtitle, extractTitle } from "./AiSearchResultCard.utils";
import { getEntityMeta } from "./entityMeta";
import { highlightMatches } from "./highlight";
import type { ICategorizedHit } from "./types";

interface IAiSearchResultCardProps {
    hit: ICategorizedHit;
    query: string;
    isHighlighted: boolean;
    isPending: boolean;
    elementId: string;
    onClick: () => void;
}

export function AiSearchResultCard(props: IAiSearchResultCardProps) {
    const { hit, query, isHighlighted, isPending, elementId, onClick } = props;
    const meta = getEntityMeta(hit.entity);
    const title = extractTitle(hit);
    const subtitle = extractSubtitle(hit);

    const titleContent: ReactNode = query ? highlightMatches(title, query) : title;

    return (
        <div
            className={cn("ai-search-result-card", {
                "ai-search-result-card--highlighted": isHighlighted,
                "ai-search-result-card--pending": isPending,
            })}
            id={elementId}
            role="option"
            tabIndex={0}
            aria-selected={isHighlighted}
            aria-busy={isPending}
            onClick={isPending ? undefined : onClick}
            data-testid="ai-search-result-card"
        >
            <div className="ai-search-result-card-row">
                <div className="ai-search-result-icon" aria-hidden="true">
                    {meta.icon}
                </div>
                <div className="ai-search-result-content">
                    <div className="ai-search-result-top">
                        <span className="ai-search-result-entity">{meta.label}</span>
                    </div>
                    <BTText size="distinct-md" tag="span" className="ai-search-result-title">
                        {titleContent}
                    </BTText>
                    {subtitle && <div className="ai-search-result-subtitle">{subtitle}</div>}
                </div>
            </div>
        </div>
    );
}
